
import * as olProj from 'ol/proj';
import Style from "ol/style/Style";
import TextStyle from "ol/style/Text";
import Fill from "ol/style/Fill";
import Projection from "ol/proj/Projection";
import VectorLayer from "ol/layer/Vector";
import VectorSource from 'ol/source/Vector';
import { Feature } from "ol";
import Point from "ol/geom/Point";
import * as olSphere from 'ol/sphere';

import {
    AppStorage,
    GazetteerSearchType,
    IGazetteerResult,
    IMap,
    IMappingTextPack
} from 'esdm-map';


export abstract class LhlpGazetteerBase {

    private _initialised: boolean = false;
    private _displayProjection: Projection;
    private _input: JQuery<HTMLInputElement>;
    private _resultsSelect: JQuery<HTMLSelectElement>;
    private _noResults: JQuery<HTMLElement>;
    private _loadingIndicator: JQuery<HTMLElement>;
    private _element: JQuery<HTMLElement>;
    private _searchResults: Array<IGazetteerResult>;
    private _pointLayer: VectorLayer;
    private _storage: AppStorage;

    public locationPinStyle: Style;

    get displayProjection(): Projection {
        return this._displayProjection;
    }

    get input(): JQuery<HTMLInputElement> {
        return this._input;
    }

    get resultsSelect(): JQuery<HTMLSelectElement> {
        return this._resultsSelect;
    }

    get noResults(): JQuery<HTMLElement> {
        return this._noResults;
    }

    get loadingIndicator(): JQuery<HTMLElement> {
        return this._loadingIndicator;
    }

    get element(): JQuery<HTMLElement> {
        return this._element;
    }

    get searchResults(): Array<IGazetteerResult> {
        return this._searchResults;
    }
    set searchResults(results: Array<IGazetteerResult>) {
        this._searchResults = results;
    }
    get pointLayer(): VectorLayer {
        return this._pointLayer;
    }

    constructor(
        protected _map: IMap,
        protected _textPack: IMappingTextPack,
        protected _apikey: string = "",
        protected _elementId: string = "gazetteer"
    ) {
        this.initBase();
    }

    private initBase = (): void => {
        if (!this._initialised) {
            //strip-loader should take this out for production
            console.log("Initialising geonames");

            this._storage = new AppStorage();

            this._displayProjection = this._map.olMap.getView().getProjection();
            this._element = $(`#${this._elementId}`);
            this.initHtml();

            //get an handle on all the html elements
            this._resultsSelect = <JQuery<HTMLSelectElement>>$("#gazetteerSelect");
            this._input = <JQuery<HTMLInputElement>>$("#gazetteerInput");
            this._noResults = $("#noResults");
            this._loadingIndicator = $("#gazLoading");
            this.initPointLayer();
            this.addHandlers();
            this._initialised = true;
        }
    }

    protected initPointLayer = (): void => {
        this._pointLayer = new VectorLayer({
            source: new VectorSource()
        });

        this.locationPinStyle = new Style({
            text: new TextStyle({
                text: '\uf3c5',
                font: 'normal 52px "Font Awesome 6 Pro"',
                textBaseline: 'bottom',
                fill: new Fill({
                    color: '#ff0000'
                })
            })
        });

        this._map.olMap.addLayer(this._pointLayer);
    }

    protected initHtml = (): void => {
        let gazetterHtml = `
                <div class="row gx-2 mb-3 has-feedback">
                    <div class="input-group">
                        <label class="sr-only" for="gazetteerInput">${this._textPack.locationPostcode}</label>
                        <input id="gazetteerInput" type="text" class="form-control hasclear"  autocomplete="off" placeholder="${this._textPack.locationPostcode}" />
                        <span class="clearer form-control-feedback"><i class="fa fa-times" aria-hidden="true"></i></span>
                        <div class="input-group-append">
                            <button id="btnSearch" class="btn" aria-label="Search ${this._textPack.locationPostcode}"><i class="fa fa-search" aria-hidden="true"></i></button>
                        </div>    
                    </div>
                </div>

                <div class="row mb-2 form-check">
                   <div class="col-auto">
                        <input type="checkbox" class="form-check-input" id="gazetteerSearchStreets">
                    </div>
                    <div class="col-auto">
                       <label class="ml-2" for="gazetteerSearchStreets">Search road/street names (can be slow)?</label>
                     </div>
                </div>

                <div class="row mb-3">
                    <label class="sr-only" for="gazetteerSelect">Select ${this._textPack.locationPostcode}</label>
                    <select id="gazetteerSelect"
                            style="display: none;overflow-x:auto;"
                            class="form-control"
                            size="1"></select>
                </div>

                <div class="row mb-2">                   
                    <div id="gazLoading" class="gazLoading" style="display:none">
                        <div class="line-scale"><div></div><div></div><div></div><div></div><div></div></div>
                    </div>
                    <span id="noResults" style="display:none">${this._textPack.noResultsFound}</span>
                </div>
         `;
        this._element.append(gazetterHtml);
    }

    protected addHandlers = (): void => {
        let self = this;

        var searchStreets = self._storage.getValue("gazetteerSearchStreets") == "true";
        if (searchStreets) {
            $("#gazetteerSearchStreets").prop("checked", true);
        }

        $("#gazetteerSearchStreets").on("change", function () {
            var val = $(this).prop("checked");
            self._storage.setValue("gazetteerSearchStreets", val);
        });

        //setup the clearer on the input textbox
        $(".hasclear").keyup(function () {
            let t = $(this);
            t.next('span').toggle(Boolean(t.val()));
        });
        $(".clearer").hide(function () {
            $(this).prev('input').val();
        });
        $(".clearer").click(function () {
            $(this).prev('input').val('').focus();
            $(this).hide();
            self._resultsSelect.empty();
            self._resultsSelect.hide();
            self._noResults.hide();
            self._pointLayer.getSource().clear();
        });

        //set the input click and enter key press handlers
        self._input.keypress(function (e) {
            if (e.which === 13) {
                self.find(<string>self._input.val());
            }
        });
        self._input.nextAll(".input-group-append").click(function () {
            self.find(<string>self._input.val());
        });
        self._resultsSelect.change(function (event) {
            self.showOnMap();
        });
    }

    protected abstract find(searchTerm: string): void;

    protected showOnMap = (): void => {
        let i = parseInt(this._resultsSelect[0][this._resultsSelect.prop("selectedIndex")].dataset.resultIndex);

        if (this._searchResults.length > 0 && this._searchResults.length >= i) {
            let coord: [number, number] = [this._searchResults[i].Easting, this._searchResults[i].Northing];
            this.addLocationPin(coord);
            this.addPinAndCenterMap(coord, this._searchResults[i].ZoomLevel);
        }
    }

    protected addPinAndCenterMap = (coord: [number, number], zoomToLevel: number): void => {
        this.addLocationPin(coord);
        this._map.olMap.getView().setCenter(coord);
        this._map.olMap.getView().setZoom(zoomToLevel);
    }

    protected addLocationPin = (displayCoordinates: [number, number]): void => {
        let locationFeature = new Feature({
            geometry: new Point(displayCoordinates),
        });

        locationFeature.setStyle(this.locationPinStyle);
        this._pointLayer.getSource().clear();
        this._pointLayer.getSource().addFeature(locationFeature);
    }

    protected getDistanceFromMapCenter = (lonlat: [number, number]): number => {
        let mapCenterLonLat = olProj.toLonLat(this._map.olMap.getView().getCenter(), this._map.options.displayProjection)
        return olSphere.getDistance(mapCenterLonLat, lonlat);
    }

}